import { Inject, Injectable } from '@angular/core';
import { Observable, SchedulerLike as IScheduler, merge, interval, concat, of, BehaviorSubject } from 'rxjs';
import { withLatestFrom, filter, exhaustMap, skip, mapTo, throttleTime, map, delay, takeUntil } from 'rxjs/operators';
import { BackgroundTaskFactory } from '../background-task/background-task.factory';
import { IPageVisibilityService, I_PAGE_VISIBILITY_SERVICE } from '../page-visibility/common/i-page-visibility.service';
import {
  IConnectionAvailabilityService,
  I_CONNECTION_AVAILABILITY_SERVICE,
} from '../connection-availability/common/i-connection-availability.service';
import { RecurringTaskAction } from './models/recurring-task.action';

/** This service is used to avoid problems with timer for SSR and e2e tests */
@Injectable({ providedIn: 'root' })
export class RecurringTaskService {
  public constructor(
    @Inject(I_PAGE_VISIBILITY_SERVICE) private readonly pageVisibilityService: IPageVisibilityService,
    @Inject(I_CONNECTION_AVAILABILITY_SERVICE) private readonly connectionAvailabilityService: IConnectionAvailabilityService,
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
  ) {}

  /** Gets an Observable that emits fetched data (using the given method) at a given interval of time.
 * Data fetch only happens when the previous fetch is done.
 * @param  fetchDataMethod - The data fetch function
 * @param  periodInMs - The interval period in milliseconds
 * @param  scheduler - Optionnal Scheduler to use.
 * @param  throttleTime - By default it is 2500 ms - the half of the min interval for refresh in new web app.
 * @param  action$ - Refresh subject - Refresh a data immediately, when it is necessary.
 * @return An Observable that emits fetched data or any error occuring during the fetch.
 * @example
 this.recurringTaskService.autoFetchData(() => myService.getData(), 10000)
 .retry() // Keep going on when an error occurs in data fetch.
 .subscribe(fetchedData => console.log(fetchedData));
 */
  public startAutoFetchData<T>(
    firstFetchDataMethod: (intervalId: number) => Observable<T>,
    fetchDataMethod: (intervalId: number) => Observable<T>,
    periodInMs: number,
    firstPeriodInMs?: number,
    scheduler?: IScheduler,
    throttleT: number = 1000,
    action$: BehaviorSubject<RecurringTaskAction> = null,
  ): Observable<T> {
    return merge(firstFetchDataMethod(0), this.autoFetchData(fetchDataMethod, periodInMs, firstPeriodInMs, scheduler, throttleT, action$));
  }

  /** Gets an Observable that emits fetched data (using the given method) at a given interval of time.
   * Data fetch only happens when the previous fetch is done.
   * @param fetchDataMethod - The data fetch function
   * @param periodInMs - The interval period in milliseconds
   * @param scheduler - Optionnal Scheduler to use.
   * @param throttleTime - By default it is 2500 ms - the half of the min interval for refresh in new web app.
   * @param action$ - Refresh subject - Refresh a data immediately, when it is necessary.
   * @return An Observable that emits fetched data or any error occuring during the fetch.
   * @example
   this.recurringTaskService.autoFetchData(() => myService.getData(), 10000)
   .retry() // Keep going on when an error occurs in data fetch.
   .subscribe(fetchedData => console.log(fetchedData));
   */
  public autoFetchData<T>(
    fetchDataMethod: (intervalId: number) => Observable<T>,
    periodInMs: number,
    firstPeriod?: number,
    scheduler?: IScheduler,
    throttleT: number = 1000,
    action$: BehaviorSubject<RecurringTaskAction> = null,
  ): Observable<T> {
    if (!fetchDataMethod) {
      throw new Error('fetchDataMethod should be defined.');
    }
    if (!periodInMs) {
      throw new Error('periodInMs should be defined.');
    }

    let taskObservable = this.getRecurringTask(periodInMs, firstPeriod, scheduler);

    const visibilityObservable = this.getVisibility();
    const availabilityObservable = this.getAvailability();

    let currentIntervalId = 0;
    const calculateIntervalId = (intervalId: number | void | boolean) => {
      if (typeof intervalId === 'number') {
        currentIntervalId = intervalId;
      } else {
        intervalId = currentIntervalId = currentIntervalId + 0.001;
      }

      return intervalId;
    };

    taskObservable = merge(taskObservable, visibilityObservable, availabilityObservable).pipe(map(calculateIntervalId));

    taskObservable = taskObservable.pipe(throttleTime(throttleT, scheduler));

    if (action$) {
      taskObservable = merge(
        taskObservable,
        // tslint:disable-next-line:no-bitwise
        action$.pipe(filter((refresh) => (refresh & RecurringTaskAction.Execute) === RecurringTaskAction.Execute)),
      ).pipe(
        map(calculateIntervalId),
        // tslint:disable-next-line:no-bitwise
        filter(() => (action$.value & RecurringTaskAction.Pause) !== RecurringTaskAction.Pause),
      );

      const loadData = fetchDataMethod;
      fetchDataMethod = (intervalId) =>
        loadData(intervalId).pipe(
          // tslint:disable-next-line:no-bitwise
          filter(() => (action$.value & RecurringTaskAction.Pause) !== RecurringTaskAction.Pause),
          takeUntil(
            action$.pipe(
              // tslint:disable-next-line:no-bitwise
              filter((refresh) => (refresh & RecurringTaskAction.Cancel) === RecurringTaskAction.Cancel),
            ),
          ),
        );
    }

    return taskObservable.pipe(
      exhaustMap((intervalId) => fetchDataMethod(intervalId)),
      this.backgroundTaskFactory.toBackground(),
    );
  }

  public getVisibility(): Observable<void> {
    return this.pageVisibilityService.isVisible.pipe(
      skip(1),
      filter((isVisible) => isVisible),
      mapTo(undefined),
    );
  }

  public getAvailability(): Observable<void> {
    return this.connectionAvailabilityService.isConnectionAvailable.pipe(
      skip(1),
      filter((isConnectionAvailable) => isConnectionAvailable),
      mapTo(undefined),
    );
  }

  protected getRecurringTask(period: number, firstPeriod?: number, scheduler?: IScheduler): Observable<number> {
    let interval$ = interval(period, scheduler);
    if (firstPeriod) {
      interval$ = concat(of(1).pipe(delay(firstPeriod, scheduler)), interval$.pipe(map((intervalId) => intervalId + 2)));
    } else {
      interval$ = interval$.pipe(map((intervalId) => intervalId + 1));
    }

    return interval$.pipe(
      withLatestFrom(this.pageVisibilityService.isVisible, this.connectionAvailabilityService.isConnectionAvailable),
      filter(([, isVisible, isConnectionAvailable]) => isVisible && isConnectionAvailable),
      map(([intervalId]) => intervalId),
    );
  }
}
